import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { api, type Json } from './api';

/** Reference lists (accounts, categories, parties, money accounts, tax sections, settings…) in one call. */
export function useReference() {
  const q = useQuery({ queryKey: ['reference'], queryFn: () => api.reference(), staleTime: 60_000 });
  const r = (q.data ?? {}) as Json;
  return {
    ...q,
    ref: r,
    accounts: (r.accounts ?? []) as Json[],
    categories: (r.categories ?? []) as Json[],
    moneyAccounts: (r.money_accounts ?? []) as Json[],
    tdsSections: (r.tds_sections ?? []) as Json[],
    parties: (r.parties ?? []) as Json[],
    financialYears: (r.financial_years ?? []) as Json[],
    settings: (r.settings ?? {}) as Json,
    partners: (r.partners ?? []) as Json[],
  };
}

/** Invalidate the query keys that a mutation touched. */
export function useInvalidate() {
  const qc = useQueryClient();
  return useCallback((...keys: (string | unknown[])[]) => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: Array.isArray(k) ? k : [k] }))), [qc]);
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), intervalMs); return () => clearInterval(t); }, [intervalMs]);
  return now;
}

/** URL search params as a simple object with a setter that keeps the rest. */
export function useSearchState(defaults: Record<string, string> = {}) {
  const [params, setParams] = useState(() => new URLSearchParams(window.location.search));
  const get = (k: string) => params.get(k) ?? defaults[k] ?? '';
  const set = (patch: Record<string, string | null | undefined>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v == null || v === '') p.delete(k); else p.set(k, v); }
    window.history.replaceState(null, '', `${window.location.pathname}${p.toString() ? `?${p}` : ''}`);
    setParams(p);
  };
  return { get, set, params };
}
